import { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { TrendingUp, TrendingDown, ExternalLink, Info, AlertCircle, HelpCircle, ChevronDown, ChevronUp, Calculator } from "lucide-react";
import { translations, getPostUrl } from "@/i18n/translations";
import { formatNumber, getPlatformBadgeClass, API, PLATFORMS } from "./constants";

// Label badge styles
const LABEL_STYLES = {
  TOP_PERFORMER: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  NEED_ATTENTION: "bg-red-500/20 text-red-400 border-red-500/30",
  NORMAL: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  INSUFFICIENT_DATA: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
};

const LABEL_NAMES = {
  en: {
    TOP_PERFORMER: "Top",
    NEED_ATTENTION: "Attention",
    NORMAL: "Normal",
    INSUFFICIENT_DATA: "Insufficient",
  },
  ko: {
    TOP_PERFORMER: "우수",
    NEED_ATTENTION: "주의",
    NORMAL: "보통",
    INSUFFICIENT_DATA: "데이터 부족",
  },
  zh: {
    TOP_PERFORMER: "优秀",
    NEED_ATTENTION: "需关注",
    NORMAL: "正常",
    INSUFFICIENT_DATA: "数据不足",
  },
};

// Engagement Guide translations
const ENGAGEMENT_GUIDE_TEXT = {
  en: {
    title: "Engagement Metrics Guide",
    description: "How we calculate and evaluate post performance",
    viewsBased: "Views-Based Platforms",
    viewsBasedDesc: "Engagement Rate (ER%) = Total Interactions / Views × 100",
    weightedSum: "Weighted Score Platforms",
    weightedSumDesc: "Weighted Score = Likes×1 + Comments×3 + Shares×5 + Saves×4",
    platform: "Platform",
    formula: "Formula",
    topThreshold: "Top",
    naThreshold: "Attention",
    minData: "Min Data",
    labelExplanation: "Label Criteria",
    topExplain: "Posts that meet the Top threshold OR rank in the top 20% within their platform",
    naExplain: "Posts below the Attention threshold OR rank in the bottom 20% within their platform",
    insufficientExplain: "Posts with insufficient data (below minimum views/likes requirement)",
    note: "Note: Comparisons are made only within the same platform for fair evaluation.",
  },
  ko: {
    title: "인게이지먼트 지표 가이드",
    description: "게시물 성과 계산 및 평가 방법",
    viewsBased: "조회수 기반 플랫폼",
    viewsBasedDesc: "인게이지먼트율 (ER%) = 총 인터랙션 / 조회수 × 100",
    weightedSum: "가중치 점수 플랫폼",
    weightedSumDesc: "가중치 점수 = 좋아요×1 + 댓글×3 + 공유×5 + 저장×4",
    platform: "플랫폼",
    formula: "계산 공식",
    topThreshold: "우수",
    naThreshold: "주의",
    minData: "최소 데이터",
    labelExplanation: "라벨 기준",
    topExplain: "우수 기준 이상 또는 플랫폼 내 상위 20%에 해당하는 게시물",
    naExplain: "주의 기준 미만 또는 플랫폼 내 하위 20%에 해당하는 게시물",
    insufficientExplain: "최소 조회수/좋아요 요건을 충족하지 못한 게시물",
    note: "참고: 공정한 평가를 위해 동일 플랫폼 내에서만 비교합니다.",
  },
  zh: {
    title: "互动指标指南",
    description: "帖子表现计算与评估方法",
    viewsBased: "基于播放量的平台",
    viewsBasedDesc: "互动率 (ER%) = 总互动 / 播放量 × 100",
    weightedSum: "加权分数平台",
    weightedSumDesc: "加权分数 = 点赞×1 + 评论×3 + 分享×5 + 收藏×4",
    platform: "平台",
    formula: "计算公式",
    topThreshold: "优秀",
    naThreshold: "需关注",
    minData: "最小数据",
    labelExplanation: "标签标准",
    topExplain: "达到优秀阈值或在平台内排名前20%的帖子",
    naExplain: "低于关注阈值或在平台内排名后20%的帖子",
    insufficientExplain: "未满足最低播放量/点赞要求的帖子",
    note: "注意：为公平评估，仅在同一平台内进行比较。",
  },
};

// Platform engagement config data
const PLATFORM_ENGAGEMENT_CONFIG = [
  {
    id: "instagram",
    name: { en: "Instagram", ko: "인스타그램", zh: "Instagram" },
    type: "views",
    formula: { en: "(Likes + Comments + Shares) / Views × 100", ko: "(좋아요 + 댓글 + 공유) / 조회수 × 100", zh: "(点赞 + 评论 + 分享) / 播放量 × 100" },
    topThreshold: "≥12%",
    naThreshold: "<7%",
    minData: "1,000 views",
  },
  {
    id: "tiktok",
    name: { en: "TikTok", ko: "틱톡", zh: "TikTok" },
    type: "views",
    formula: { en: "(Likes + Comments + Shares + Saves) / Views × 100", ko: "(좋아요 + 댓글 + 공유 + 저장) / 조회수 × 100", zh: "(点赞 + 评论 + 分享 + 收藏) / 播放量 × 100" },
    topThreshold: "≥20%",
    naThreshold: "<9%",
    minData: "1,000 views",
  },
  {
    id: "youtube",
    name: { en: "YouTube", ko: "유튜브", zh: "YouTube" },
    type: "views",
    formula: { en: "(Likes + Comments) / Views × 100", ko: "(좋아요 + 댓글) / 조회수 × 100", zh: "(点赞 + 评论) / 播放量 × 100" },
    topThreshold: "≥10%",
    naThreshold: "<6%",
    minData: "1,000 views",
  },
  {
    id: "bilibili",
    name: { en: "Bilibili", ko: "빌리빌리", zh: "哔哩哔哩" },
    type: "views",
    formula: { en: "(Likes + Comments + Shares + Saves) / Views × 100", ko: "(좋아요 + 댓글 + 공유 + 저장) / 조회수 × 100", zh: "(点赞 + 评论 + 分享 + 收藏) / 播放量 × 100" },
    topThreshold: "≥10%",
    naThreshold: "<6%",
    minData: "1,000 views",
  },
  {
    id: "weibo",
    name: { en: "Weibo", ko: "웨이보", zh: "微博" },
    type: "weighted",
    formula: { en: "Likes×1 + Comments×3 + Shares×5", ko: "좋아요×1 + 댓글×3 + 공유×5", zh: "点赞×1 + 评论×3 + 分享×5" },
    topThreshold: "P80",
    naThreshold: "P20",
    minData: { en: "10 score", ko: "10점", zh: "10分" },
  },
  {
    id: "xiaohongshu",
    name: { en: "Xiaohongshu", ko: "샤오홍슈", zh: "小红书" },
    type: "weighted",
    formula: { en: "Likes×1 + Comments×3 + Shares×5 + Saves×4", ko: "좋아요×1 + 댓글×3 + 공유×5 + 저장×4", zh: "点赞×1 + 评论×3 + 分享×5 + 收藏×4" },
    topThreshold: "P80",
    naThreshold: "P20",
    minData: { en: "10 score", ko: "10점", zh: "10分" },
  },
  {
    id: "douyin",
    name: { en: "Douyin", ko: "더우인", zh: "抖音" },
    type: "weighted",
    formula: { en: "Likes×1 + Comments×3 + Shares×5 + Saves×4", ko: "좋아요×1 + 댓글×3 + 공유×5 + 저장×4", zh: "点赞×1 + 评论×3 + 分享×5 + 收藏×4" },
    topThreshold: "P80",
    naThreshold: "P20",
    minData: { en: "10 score", ko: "10점", zh: "10分" },
  },
];

// Engagement Guide Component
export const EngagementGuide = ({ lang = "en" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const t = ENGAGEMENT_GUIDE_TEXT[lang] || ENGAGEMENT_GUIDE_TEXT.en;
  
  const viewsBasedPlatforms = PLATFORM_ENGAGEMENT_CONFIG.filter(p => p.type === "views");
  const weightedPlatforms = PLATFORM_ENGAGEMENT_CONFIG.filter(p => p.type === "weighted");
  
  return (
    <Card className="bg-card border-border/50 mb-6">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calculator className="h-5 w-5 text-blue-400" />
                <CardTitle className="text-lg font-heading">{t.title}</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{t.description}</span>
                {isOpen ? (
                  <ChevronUp className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            <div className="space-y-6">
              {/* Views-based platforms section */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30">
                    {t.viewsBased}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{t.viewsBasedDesc}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/50">
                        <th className="text-left py-2 px-3 text-muted-foreground font-medium">{t.platform}</th>
                        <th className="text-left py-2 px-3 text-muted-foreground font-medium">{t.formula}</th>
                        <th className="text-center py-2 px-3 text-emerald-400 font-medium">{t.topThreshold}</th>
                        <th className="text-center py-2 px-3 text-red-400 font-medium">{t.naThreshold}</th>
                        <th className="text-center py-2 px-3 text-muted-foreground font-medium">{t.minData}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewsBasedPlatforms.map((p) => (
                        <tr key={p.id} className="border-b border-border/30 hover:bg-muted/20">
                          <td className="py-2 px-3">
                            <Badge variant="outline" className={`${getPlatformBadgeClass(p.id)} text-xs`}>
                              {p.name[lang] || p.name.en}
                            </Badge>
                          </td>
                          <td className="py-2 px-3 text-xs font-mono text-foreground/80">{p.formula[lang] || p.formula.en}</td>
                          <td className="py-2 px-3 text-center text-emerald-400 font-mono">{p.topThreshold}</td>
                          <td className="py-2 px-3 text-center text-red-400 font-mono">{p.naThreshold}</td>
                          <td className="py-2 px-3 text-center text-muted-foreground text-xs">{p.minData}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Weighted score platforms section */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="outline" className="bg-pink-500/10 text-pink-400 border-pink-500/30">
                    {t.weightedSum}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{t.weightedSumDesc}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/50">
                        <th className="text-left py-2 px-3 text-muted-foreground font-medium">{t.platform}</th>
                        <th className="text-left py-2 px-3 text-muted-foreground font-medium">{t.formula}</th>
                        <th className="text-center py-2 px-3 text-emerald-400 font-medium">{t.topThreshold}</th>
                        <th className="text-center py-2 px-3 text-red-400 font-medium">{t.naThreshold}</th>
                        <th className="text-center py-2 px-3 text-muted-foreground font-medium">{t.minData}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {weightedPlatforms.map((p) => (
                        <tr key={p.id} className="border-b border-border/30 hover:bg-muted/20">
                          <td className="py-2 px-3">
                            <Badge variant="outline" className={`${getPlatformBadgeClass(p.id)} text-xs`}>
                              {p.name[lang] || p.name.en}
                            </Badge>
                          </td>
                          <td className="py-2 px-3 text-xs font-mono text-foreground/80">{p.formula[lang] || p.formula.en}</td>
                          <td className="py-2 px-3 text-center text-emerald-400 font-mono">{p.topThreshold}</td>
                          <td className="py-2 px-3 text-center text-red-400 font-mono">{p.naThreshold}</td>
                          <td className="py-2 px-3 text-center text-muted-foreground text-xs">
                            {typeof p.minData === 'object' ? (p.minData[lang] || p.minData.en) : p.minData}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Label explanations */}
              <div className="bg-muted/20 rounded-lg p-4">
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Info className="h-4 w-4 text-blue-400" />
                  {t.labelExplanation}
                </h4>
                <div className="space-y-2 text-xs">
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className={`${LABEL_STYLES.TOP_PERFORMER} shrink-0`}>
                      {LABEL_NAMES[lang]?.TOP_PERFORMER || "Top"}
                    </Badge>
                    <span className="text-muted-foreground">{t.topExplain}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className={`${LABEL_STYLES.NEED_ATTENTION} shrink-0`}>
                      {LABEL_NAMES[lang]?.NEED_ATTENTION || "Attention"}
                    </Badge>
                    <span className="text-muted-foreground">{t.naExplain}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className={`${LABEL_STYLES.INSUFFICIENT_DATA} shrink-0`}>
                      {LABEL_NAMES[lang]?.INSUFFICIENT_DATA || "Insufficient"}
                    </Badge>
                    <span className="text-muted-foreground">{t.insufficientExplain}</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border/30">
                  💡 {t.note}
                </p>
              </div>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

// Post card component with engagement score
const PerformancePostCard = ({ post, index, type, accounts, lang }) => {
  const t = translations[lang] || translations.en;
  const account = accounts?.find(a => a.id === post.account_id);
  const postUrl = post.post_url || getPostUrl(post.platform, post.post_id, account?.username);
  
  const isTopType = type === "top";
  const colorClass = isTopType ? "text-emerald-500" : "text-red-500";
  const hoverClass = isTopType ? "hover:bg-emerald-500/10" : "hover:bg-red-500/10";
  
  // Format engagement score based on metric type
  const formatScore = (score, platform, metricType) => {
    if (score === null || score === undefined) return "N/A";
    
    // Use actual metric type from backend (handles fallback correctly)
    if (metricType === "WEIGHTED_SUM") {
      return score.toLocaleString();
    } else if (metricType === "ER_PERCENT") {
      return `${score.toFixed(2)}%`;
    }
    
    // Fallback: determine by platform
    const viewsBasedPlatforms = ["instagram", "tiktok", "youtube", "bilibili"];
    const weightedPlatforms = ["weibo", "xiaohongshu", "douyin"];
    
    if (viewsBasedPlatforms.includes(platform)) {
      return `${score.toFixed(2)}%`;
    } else if (weightedPlatforms.includes(platform)) {
      return score.toLocaleString();
    } else {
      return score.toFixed(4);
    }
  };
  
  const scoreLabel = post.engagement_score !== null 
    ? formatScore(post.engagement_score, post.platform, post.metric_type_used)
    : "N/A";
  
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <span className={`text-lg font-bold ${colorClass} font-mono`}>#{index + 1}</span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground truncate max-w-[120px] sm:max-w-[180px]">{post.title || "Untitled"}</p>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className={`${getPlatformBadgeClass(post.platform)} text-xs`}>
              {post.platform}
            </Badge>
            {post.label && post.label !== "NORMAL" && (
              <Badge variant="outline" className={`${LABEL_STYLES[post.label]} text-xs`}>
                {LABEL_NAMES[lang]?.[post.label] || post.label}
              </Badge>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-right">
          <p className={`font-mono text-sm ${colorClass}`}>
            {scoreLabel}
          </p>
          {post.label_reasons && post.label_reasons.length > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-end gap-1 cursor-help">
                    <Info className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      {post.label_reasons.length > 1 
                        ? `${post.label_reasons.length} reasons` 
                        : "1 reason"}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-xs">
                  <ul className="text-xs space-y-1">
                    {post.label_reasons.map((reason, i) => (
                      <li key={i}>• {reason}</li>
                    ))}
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {postUrl && (
          <a
            href={postUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`p-1.5 rounded-md ${hoverClass} transition-colors ${colorClass}`}
            title={t.posts?.openOriginal || "Open original"}
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
};

// Platform filter dropdown
const PlatformFilter = ({ value, onChange, platforms, lang }) => {
  const t = translations[lang] || translations.en;
  
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[140px] h-8 text-xs bg-card border-border/50">
        <SelectValue placeholder={t.header?.allPlatforms || "All Platforms"} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{t.header?.allPlatforms || "All Platforms"}</SelectItem>
        {platforms.map(p => (
          <SelectItem key={p.id} value={p.id}>
            {p.name?.[lang] || p.name?.en || p.id}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

// Main Top Performers section with platform filter
export const TopPerformersSection = ({ 
  summary, 
  accounts, 
  lang, 
  startDate, 
  endDate,
  accountIds 
}) => {
  const t = translations[lang] || translations.en;
  const [platformFilter, setPlatformFilter] = useState("all");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [platformSummary, setPlatformSummary] = useState(null);
  
  // Available platforms from data
  const availablePlatforms = PLATFORMS.filter(p => p.id !== "all");
  
  // Fetch filtered data when platform changes
  useEffect(() => {
    const fetchFilteredPosts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (platformFilter && platformFilter !== "all") {
          params.append("platform", platformFilter);
        }
        // Format dates properly as yyyy-MM-dd strings
        if (startDate) {
          const sd = startDate instanceof Date ? format(startDate, "yyyy-MM-dd") : String(startDate).slice(0, 10);
          params.append("start_date", sd);
        }
        if (endDate) {
          const ed = endDate instanceof Date ? format(endDate, "yyyy-MM-dd") : String(endDate).slice(0, 10);
          params.append("end_date", ed);
        }
        if (accountIds && accountIds.length > 0) {
          params.append("account_ids", accountIds.join(","));
        }
        
        const url = `${API}/dashboard/top-performers?${params.toString()}`;
        console.log("[TopPerformers] API call:", url);
        const response = await axios.get(url);
        const fetchedPosts = response.data.posts || [];
        console.log("[TopPerformers] Received posts:", fetchedPosts.length, fetchedPosts.map(p => ({ title: p.title?.slice(0,30), published_at: p.published_at })));
        setPosts(fetchedPosts);
        setPlatformSummary(response.data.platform_summary);
      } catch (error) {
        console.error("[TopPerformers] API error:", error);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFilteredPosts();
  }, [platformFilter, startDate, endDate, accountIds]);
  
  return (
    <Card className="bg-card border-border/50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-500" />
            <CardTitle className="text-lg font-heading">{t.posts?.topPerforming || "Top Performing"}</CardTitle>
          </div>
          <PlatformFilter 
            value={platformFilter} 
            onChange={setPlatformFilter}
            platforms={availablePlatforms}
            lang={lang}
          />
        </div>
        {platformSummary && (
          <div className="mt-2 text-xs text-muted-foreground bg-muted/30 p-2 rounded-md">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <span>📊 {platformSummary.formula_description}</span>
              <span>🎯 Top ≥ {platformSummary.thresholds?.top}</span>
              {platformSummary.score_stats && (
                <span>📈 Median: {platformSummary.score_stats.median}</span>
              )}
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : posts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>{lang === "ko" ? "데이터가 없습니다" : lang === "zh" ? "暂无数据" : "No data available"}</p>
            </div>
          ) : (
            posts.slice(0, 6).map((post, i) => (
              <PerformancePostCard 
                key={post.id || post.post_id || i}
                post={post}
                index={i}
                type="top"
                accounts={accounts}
                lang={lang}
              />
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Main Need Attention section with platform filter
export const NeedAttentionSection = ({ 
  summary, 
  accounts, 
  lang, 
  startDate, 
  endDate,
  accountIds 
}) => {
  const t = translations[lang] || translations.en;
  const [platformFilter, setPlatformFilter] = useState("all");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [platformSummary, setPlatformSummary] = useState(null);
  
  const availablePlatforms = PLATFORMS.filter(p => p.id !== "all");
  
  useEffect(() => {
    const fetchFilteredPosts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (platformFilter && platformFilter !== "all") {
          params.append("platform", platformFilter);
        }
        // Format dates properly as yyyy-MM-dd strings
        if (startDate) {
          const sd = startDate instanceof Date ? format(startDate, "yyyy-MM-dd") : String(startDate).slice(0, 10);
          params.append("start_date", sd);
        }
        if (endDate) {
          const ed = endDate instanceof Date ? format(endDate, "yyyy-MM-dd") : String(endDate).slice(0, 10);
          params.append("end_date", ed);
        }
        if (accountIds && accountIds.length > 0) {
          params.append("account_ids", accountIds.join(","));
        }
        
        const url = `${API}/dashboard/need-attention?${params.toString()}`;
        console.log("[NeedAttention] API call:", url);
        const response = await axios.get(url);
        const fetchedPosts = response.data.posts || [];
        console.log("[NeedAttention] Received posts:", fetchedPosts.length, fetchedPosts.map(p => ({ title: p.title?.slice(0,30), published_at: p.published_at })));
        setPosts(fetchedPosts);
        setPlatformSummary(response.data.platform_summary);
      } catch (error) {
        console.error("[NeedAttention] API error:", error);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFilteredPosts();
  }, [platformFilter, startDate, endDate, accountIds]);
  
  return (
    <Card className="bg-card border-border/50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <TrendingDown className="h-5 w-5 text-red-500" />
            <CardTitle className="text-lg font-heading">{t.posts?.needsAttention || "Needs Attention"}</CardTitle>
          </div>
          <PlatformFilter 
            value={platformFilter} 
            onChange={setPlatformFilter}
            platforms={availablePlatforms}
            lang={lang}
          />
        </div>
        {platformSummary && (
          <div className="mt-2 text-xs text-muted-foreground bg-muted/30 p-2 rounded-md">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <span>📊 {platformSummary.formula_description}</span>
              <span>⚠️ NA &lt; {platformSummary.thresholds?.need_attention}</span>
              {platformSummary.score_stats && (
                <span>📈 Median: {platformSummary.score_stats.median}</span>
              )}
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : posts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>{lang === "ko" ? "데이터가 없습니다" : lang === "zh" ? "暂无数据" : "No data available"}</p>
            </div>
          ) : (
            posts.slice(0, 6).map((post, i) => (
              <PerformancePostCard 
                key={post.id || post.post_id || i}
                post={post}
                index={i}
                type="attention"
                accounts={accounts}
                lang={lang}
              />
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default { EngagementGuide, TopPerformersSection, NeedAttentionSection };
